/**
 * TemplateAssistant — the assistant beside an intel analysis template's editor: the platform's one form assistant
 * (`EntityAssistantDrawer`, a real conversation about the template, subject `analysis_template`, opened on the
 * template assistant persona). The form as it is goes out with every message; the persona's `template-update` block
 * becomes per-field apply pills, each written into the form on its own. Demo page: /chats (the thread) in a side Sheet.
 */
import { useState } from 'react';
import type { IntelAnalysisTemplate } from '../../generated/api/types.gen.js';
import { EntityAssistantDrawer } from '../assistant/EntityAssistantDrawer.js';

interface TemplateAssistantProps {
  /** The saved template's id; absent while the template is new. */
  templateId?: number;
  template: Partial<IntelAnalysisTemplate>;
  onTemplateUpdate: (updates: Partial<IntelAnalysisTemplate>) => void;
  availablePlatforms: string[];
  availableCategories: string[];
  existingTemplates?: Array<{ name: string; platform: string; category: string }>;
}

export function TemplateAssistant({
  templateId,
  template,
  onTemplateUpdate,
  availablePlatforms,
  availableCategories,
  existingTemplates = [],
}: TemplateAssistantProps) {
  // A new template's conversation is kept under a key for this editing session.
  const [draftKey] = useState(() => crypto.randomUUID());
  return (
    <EntityAssistantDrawer<Partial<IntelAnalysisTemplate>>
      subjectType="analysis_template"
      {...(templateId !== undefined ? { subjectId: String(templateId) } : {})}
      draftKey={draftKey}
      entryPoint="analysis-template-editor"
      currentFormValues={template}
      contextExtras={{ availablePlatforms, availableCategories, existingTemplates }}
      onApplyFieldUpdate={(partial) => onTemplateUpdate({ ...template, ...partial })}
    />
  );
}
